import { isLocalStorageAvailable } from "@kaggle/core";
import type { app } from "firebase";
import { featureFlags } from "../featureFlags";

const apps = new Map<string, Promise<app.App | null>>();

const FIRESTORE_DB_NAME = "[DEFAULT]";

const daysSinceUnixMillis = (then: number) => {
  const now = new Date();
  return (now.getTime() - then) / (1000 * 3600 * 24);
};

export interface FirebaseConfig {
  authToken: string | null;
  options: FirebaseOptions | null;
  proxyDomain: string | null;
}

export interface FirebaseOptions {
  appId: string | null;
  apiKey: string | null;
  authDomain: string | null;
  databaseUrl: string | null;
  messagingSenderId: string | null;
  projectId: string | null;
  storageBucket: string | null;
}

export type ConfigFetcherFunc = () => Promise<FirebaseConfig>;
export type OnFirestoreConnectionError = (errorMsg : string) => void;

export const ensureClientInitialized = (appName: string, signInAnonymous: boolean, configFetcherFunc: ConfigFetcherFunc, onFirebaseConnectionError? : OnFirestoreConnectionError) => {
  if (onFirebaseConnectionError) {
    errorDispatcher.addListener(appName, onFirebaseConnectionError)
  }
  if (!apps.has(appName)) {
    apps.set(appName, initializeClient(appName, signInAnonymous, configFetcherFunc));
  }
  return apps.get(appName);
}

const initializeClient = async (appName: string, signInAnonymous: boolean, configFetcherFunc: ConfigFetcherFunc) => {
  const useFirestoreProxy =
  featureFlags.kernelsFirebaseProxy ||
  (isLocalStorageAvailable() &&
    !!localStorage.useProxy &&
    daysSinceUnixMillis(localStorage.useProxyUpdateTime) < 1); // expire proxy failover after 1 day

  const firebaseConfig = await configFetcherFunc();
 
  if (!firebaseConfig.options || !firebaseConfig.proxyDomain) {
    return null;
  }

  const isLoggedIn = firebaseConfig.authToken && firebaseConfig.authToken.length > 0;
  if (!isLoggedIn && !signInAnonymous) {
    // Skip initialization for anonymous users if sign-in is disabled for them.
    return null;
  }

  const firebaseModule = await import("firebase/app");
  await import("firebase/auth");
  await import("firebase/firestore");

  let firebaseClient: app.App;
  try {
    // Throws an exception if the app hasn't been initialized yet.
    firebaseClient = firebaseModule.app(appName);
  } catch {
    firebaseClient = firebaseModule.initializeApp(firebaseConfig.options, appName);
  }

  const firestoreStatePoll = setInterval(() => {
    const firestoreService = (firebaseClient as any).services_?.firestore?.[
      FIRESTORE_DB_NAME
    ];

    if (
      !firestoreService?._firestoreClient?.remoteStore?.onlineStateTracker
    ) {
      // Retry until the app is initialized.
      return;
    }

    const firestoreState =
      firestoreService._firestoreClient.remoteStore.onlineStateTracker.state;
    switch (firestoreState) {
      case 1: // Online, stop checking.
        clearInterval(firestoreStatePoll);
        return;

      case 2: // Offline.
        clearInterval(firestoreStatePoll);

        if (!useFirestoreProxy) {
          if (isLocalStorageAvailable()) {
            // Try to reload the page with proxying turned on:
            localStorage.useProxy = true;
            const now = new Date();
            localStorage.useProxyUpdateTime = now.getTime();
            location.reload();
            return;
          }
          errorDispatcher.dispatch(appName, "Failed to connect to Firestore.");
          return;
        }
        errorDispatcher.dispatch(appName, "Failed to connect to Firestore via Jupyter.");
        return;
    }
  }, 3000);

  if (featureFlags.kernelsFirestoreDebugLogging) {
    firebaseModule.firestore.setLogLevel("debug");
  }

  if (useFirestoreProxy && firebaseConfig.proxyDomain) {
    // Intercept all XHR requests and re-point googleapis requests to the proxy.
    const realOpen = window.XMLHttpRequest.prototype.open;
    window.XMLHttpRequest.prototype.open = function(
      method: string,
      url: any,
      async?: boolean,
      user?: string,
      password?: string
    ) {
      let newURL = url.replace(
        /https:\/\/securetoken.googleapis.com\/v1\/token/,
        `https://${firebaseConfig.proxyDomain}/v1/token`
      );
      newURL = newURL.replace(
        /https:\/\/www.googleapis.com\/identitytoolkit\/v3\/relyingparty/,
        `https://${firebaseConfig.proxyDomain}/identitytoolkit/v3/relyingparty`
      );
      return realOpen.call(this, method, newURL, !!async, user, password);
    };

    // Point Firestore to the proxy.
    const db = firebaseModule.firestore(
      firebaseClient
    ) as firebase.firestore.Firestore;
    db.settings({
      host: firebaseConfig.proxyDomain,
      experimentalForceLongPolling: featureFlags.kernelsFirebaseLongPolling,
    });
  }

  firebaseConfig.authToken && firebaseConfig.authToken.length > 0 ?
    await firebaseClient.auth().signInWithCustomToken(firebaseConfig.authToken): 
    await firebaseClient.auth().signInAnonymously();
  
  return firebaseClient;
};

class FirebaseConnectionErrorDispatcher {
  private listeners =  new Map<string, Array<OnFirestoreConnectionError>>();

  addListener = (appName : string, listener : OnFirestoreConnectionError) => {
    let listenersForApp = this.listeners.get(appName);
    if (!listenersForApp) {
      listenersForApp = new Array<OnFirestoreConnectionError>();
      this.listeners.set(appName, listenersForApp);
    }
    listenersForApp.push(listener);
  }

  dispatch = (appName : string, errorMsg : string) => {
    const listenersForApp = this.listeners.get(appName);
    if (listenersForApp) {
      for (const listener of listenersForApp) {
        listener(errorMsg);
      }
    }
  }
}

const errorDispatcher = new FirebaseConnectionErrorDispatcher();